import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/solid'; // Example icons

// Define the stages of the automation process
const STAGES = [
  { id: 'INITIATING', name: 'Initiating', icon: ArrowPathIcon }, // Shortened names slightly
  { id: 'NAVIGATING', name: 'Navigating', icon: ClockIcon },
  { id: 'ADDING_ITEMS', name: 'Adding Items', icon: ClockIcon },
  { id: 'PROCEEDING_CHECKOUT', name: 'To Checkout', icon: ClockIcon },
  { id: 'FILLING_SHIPPING', name: 'Shipping Info', icon: ClockIcon },
  { id: 'FILLING_PAYMENT', name: 'Payment Info', icon: ClockIcon },
  { id: 'PLACING_ORDER', name: 'Placing Order', icon: ClockIcon },
  { id: 'WAITING_CONFIRMATION', name: 'Confirming', icon: ClockIcon },
  { id: 'COMPLETED', name: 'Completed', icon: CheckCircleIcon },
  { id: 'FAILED', name: 'Failed', icon: ExclamationCircleIcon },
];

// --- Use the environment variable ---
// Ensure REACT_APP_AUTOMATION_SERVICE_WS_URL is defined in your frontend's .env file
const AUTOMATION_STATUS_URL = process.env.REACT_APP_AUTOMATION_SERVICE_WS_URL || 'ws://localhost:3001/status'; // Fallback remains

function OrderProcessingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, vendorCount } = location.state || {}; // Assuming you pass orderId and maybe vendorCount

  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Connecting to automation service...');
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [finalOrderNumber, setFinalOrderNumber] = useState(null);
  const ws = useRef(null); // Use ref to hold the WebSocket object

  // --- Real-Time WebSocket/SSE Logic ---
  useEffect(() => {
    if (!orderId) {
        setError("Order ID missing. Cannot track status.");
        setIsComplete(true); // Mark as complete with error
        setCurrentStageIndex(STAGES.findIndex(s => s.id === 'FAILED'));
        return;
    }

    // Prevent multiple connections
    if (ws.current && ws.current.readyState !== WebSocket.CLOSED) {
        console.log("WebSocket connection already exists or closing.");
        return;
    }

    console.log(`Attempting to connect WebSocket for Order ID: ${orderId}`);
    // Construct the specific URL for this order using the base URL
    const connectionUrl = `${AUTOMATION_STATUS_URL}/${orderId}`; // This line uses the constant defined above
    ws.current = new WebSocket(connectionUrl);

    ws.current.onopen = () => {
      console.log(`WebSocket Connected for order status: ${orderId}`);
      setStatusMessage('Connected. Waiting for automation updates...');
      // Optional: Send a message if the backend requires it upon connection
      // ws.current.send(JSON.stringify({ type: 'subscribe', orderId: orderId }));
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Received status update:', data);

        // --- Update state based on backend message ---
        setStatusMessage(data.message || 'Processing...');

        // Find stage index based on backend 'stage' ID
        const stageIndex = STAGES.findIndex(s => s.id === data.stage);
        if (stageIndex !== -1) {
           setCurrentStageIndex(stageIndex); // Update the timeline position
        } else {
            console.warn(`Received unknown stage ID from backend: ${data.stage}`);
        }


        // Check for final status (COMPLETED or FAILED)
        if (data.status === 'COMPLETED') {
          setIsComplete(true);
          setError(null); // Clear any previous errors
          setFinalOrderNumber(data.orderNumber || 'N/A'); // Store order number
          // Ensure the stage is visually marked as completed
          const completeStageIndex = STAGES.findIndex(s => s.id === 'COMPLETED');
          if (completeStageIndex !== -1) setCurrentStageIndex(completeStageIndex);
           if (ws.current) ws.current.close(); // Close connection on completion
        } else if (data.status === 'FAILED') {
          setIsComplete(true);
          setError(data.error || 'An unknown error occurred during automation.');
          setFinalOrderNumber(null);
          // Set the stage index to the FAILED state for visual feedback
          const failStageIndex = STAGES.findIndex(s => s.id === 'FAILED');
          if (failStageIndex !== -1) setCurrentStageIndex(failStageIndex);
           if (ws.current) ws.current.close(); // Close connection on failure
        }

      } catch (e) {
          console.error("Failed to parse WebSocket message or update state:", e);
          // Optionally set a generic error state here
          // setError("Received invalid status update from server.");
          // setIsComplete(true);
          // setCurrentStageIndex(STAGES.findIndex(s => s.id === 'FAILED'));
      }
    };

    ws.current.onerror = (errorEvent) => {
      console.error('WebSocket Error:', errorEvent);
      // Try to provide a more specific error if possible, otherwise generic
      const errorMessage = ws.current?.readyState === WebSocket.CONNECTING
          ? 'Could not connect to the automation status service.'
          : 'Lost connection to the automation status service.';
      setError(errorMessage);
      setIsComplete(true); // Treat connection error as a final (failed) state
      const failStageIndex = STAGES.findIndex(s => s.id === 'FAILED');
      if (failStageIndex !== -1) setCurrentStageIndex(failStageIndex);
    };

    ws.current.onclose = (closeEvent) => {
      console.log(`WebSocket Disconnected for order ${orderId}. Code: ${closeEvent.code}, Reason: ${closeEvent.reason}`);
      // If the process didn't complete normally before closing, mark as failed.
      if (!isComplete) {
          // Avoid race conditions; check if already set by onmessage/onerror
          setError(prevError => prevError || 'Status connection closed unexpectedly.');
          setIsComplete(true);
           const failStageIndex = STAGES.findIndex(s => s.id === 'FAILED');
           if (failStageIndex !== -1 && !error) setCurrentStageIndex(failStageIndex); // Update stage only if not already set by error
      }
       ws.current = null; // Clear the ref
    };

    // Cleanup function: Close WebSocket connection when component unmounts
    return () => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        console.log(`Closing WebSocket connection for order ${orderId} on component unmount.`);
        ws.current.close();
        ws.current = null;
      }
    };
    // Add isComplete to dependencies? Maybe not, as we want cleanup only on unmount/orderId change.
    // Re-run if orderId changes.
  }, [orderId]);
  // --- End Real-Time WebSocket/SSE Logic ---


  if (!orderId && !error) { // Show loading or initial state briefly if orderId is loading
      return <div className="container mx-auto p-8 text-center">Checking Order ID...</div>;
  }
   // Show error immediately if orderId was missing from the start
   if (!orderId && error) {
     return (
       <div className="container mx-auto p-8 text-center">
         <h1 className="text-2xl mb-4 text-red-600">{error}</h1>
         <Link to="/" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
           Return to Home
         </Link>
       </div>
     );
   }


  // Determine the correct stage object based on error or completion status
  const effectiveStageIndex = error ? STAGES.findIndex(s => s.id === 'FAILED') : currentStageIndex;
  const finalStage = STAGES[effectiveStageIndex] || STAGES[0]; // Fallback to first stage if index is invalid
  const hasFailed = finalStage.id === 'FAILED';
  // Find the index where the failure *occurred* (might be different from the final FAILED index)
  const failurePointIndex = hasFailed ? STAGES.findIndex((stage, idx) => stage.id === STAGES[currentStageIndex]?.id) : -1;


  // Calculate progress bar width
  const totalDisplayStages = STAGES.length - 2; // Don't include Completed/Failed in span
  let progressPercent = 0;
   if (hasFailed && failurePointIndex >= 0) {
       // Stop progress at the point of failure
       progressPercent = (failurePointIndex / totalDisplayStages) * 100;
   } else if (!hasFailed && currentStageIndex <= totalDisplayStages) {
       // Normal progress or completed state
       progressPercent = (currentStageIndex / totalDisplayStages) * 100;
   } else if (finalStage.id === 'COMPLETED') {
       progressPercent = 100; // Full bar on success
   }
   // Ensure percentage stays within bounds
   progressPercent = Math.max(0, Math.min(100, progressPercent));


  return (
    <div className="container mx-auto p-6 md:p-10"> {/* Adjusted padding */}
      <h1 className="text-3xl font-bold mb-3 text-center text-slate-800">Processing Your Order</h1>
      <p className="text-center text-slate-500 mb-10">Order ID: <span className="font-semibold text-slate-600">{orderId}</span></p>

      {/* Timeline Visualization */}
      <div className="relative mb-16 px-2 md:px-4"> {/* Increased bottom margin */}
         {/* Base Track */}
        <div className="absolute left-0 top-1/2 w-full h-1.5 bg-slate-200 rounded-full transform -translate-y-1/2"></div>
         {/* Progress/Error Track */}
        <div
           className={`absolute left-0 top-1/2 h-1.5 ${hasFailed ? 'bg-rose-500' : 'bg-indigo-500'} rounded-full transform -translate-y-1/2 transition-all duration-500 ease-out`}
           style={{ width: `${progressPercent}%` }}
        ></div>

        <div className="relative flex justify-between">
          {STAGES.filter(s => s.id !== 'FAILED').map((stage, index) => { // Still exclude FAILED stage marker itself
            const isActive = index === currentStageIndex && !isComplete && !hasFailed; // Active only if not complete/failed
            const isCompleted = !hasFailed && (index < currentStageIndex || finalStage.id === 'COMPLETED');
            const isPastFailure = hasFailed && failurePointIndex !== -1 && index < failurePointIndex; // Completed before failure
            const isAtFailurePoint = hasFailed && index === failurePointIndex;

            let IconComponent = stage.icon;
            let iconColor = 'text-slate-400';
            let bgColor = 'bg-white';
            let borderColor = 'border-slate-300';
            let shadow = 'shadow-sm';

            if (isActive) {
               iconColor = 'text-indigo-500 animate-spin';
               bgColor = 'bg-indigo-50';
               borderColor = 'border-indigo-400';
               shadow = 'shadow-md';
            } else if (isCompleted || isPastFailure) {
               IconComponent = CheckCircleIcon;
               iconColor = 'text-emerald-500';
               bgColor = 'bg-emerald-50';
               borderColor = 'border-emerald-400';
               shadow = 'shadow-sm';
            } else if (isAtFailurePoint) {
                IconComponent = ExclamationCircleIcon;
                iconColor = 'text-rose-500';
                bgColor = 'bg-rose-50';
                borderColor = 'border-rose-400';
                shadow = 'shadow-md';
            }
            // Stages after failure remain default gray

            return (
              <div key={stage.id} className="relative flex flex-col items-center w-1/${totalDisplayStages + 1}"> {/* Adjust width based on stage count */}
                <div className={`relative w-9 h-9 rounded-full border-2 ${borderColor} ${bgColor} ${shadow} flex items-center justify-center z-10 mb-2 transition-all duration-300`}>
                  <IconComponent className={`h-5 w-5 ${iconColor} transition-colors duration-300`} />
                   {/* Current Status Pop-up - Adjusted Style */}
                    {(isActive || isAtFailurePoint) && !isComplete && (
                        <div className="absolute top-full mt-3 w-44 p-2.5 bg-white text-slate-700 text-xs rounded-md shadow-lg z-20 text-center ring-1 ring-slate-200">
                            {isAtFailurePoint ? 'Failed here' : statusMessage}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-white drop-shadow-sm"></div> {/* Arrow pointing up */}
                        </div>
                    )}
                </div>
                <span className={`text-xs text-center font-medium ${isActive || isCompleted || isPastFailure || isAtFailurePoint ? 'text-slate-600' : 'text-slate-400'}`}>
                   {stage.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>

       {/* Overall Status Message Area - Refined Styles */}
       <div className="mt-8 p-4 rounded-lg text-center max-w-2xl mx-auto">
            {hasFailed && (
                <div className="bg-rose-50 border border-rose-300 text-rose-800 px-4 py-3 rounded-lg relative shadow-sm" role="alert">
                    <strong className="font-semibold mr-2">Automation Failed</strong>
                    <span className="block sm:inline">{error || 'An unexpected error occurred.'}</span>
                </div>
            )}
            {!hasFailed && isComplete && finalStage.id === 'COMPLETED' && (
                <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-3 rounded-lg relative shadow-sm" role="alert">
                    <strong className="font-semibold mr-2">Success!</strong>
                    <span className="block sm:inline">{statusMessage}</span>
                    {finalOrderNumber && <p className="mt-1 text-sm">Vendor Order Number: <span className="font-medium">{finalOrderNumber}</span></p>}
                </div>
            )}
             {!isComplete && !hasFailed && (
                <div className="bg-indigo-50 border border-indigo-300 text-indigo-800 px-4 py-3 rounded-lg relative shadow-sm animate-pulse" role="status">
                    <strong className="font-semibold mr-2">In Progress...</strong>
                     <span className="block sm:inline">{statusMessage}</span>
                </div>
            )}
        </div>


       {/* Navigation Buttons - Refined Styles */}
        <div className="mt-10 flex justify-center gap-4">
            {(isComplete || hasFailed) && (
                 <>
                 <Link
                    to="/orders"
                    className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                 >
                    View Order History
                 </Link>
                 <Link
                    to="/"
                    className="inline-flex items-center justify-center px-5 py-2 border border-slate-300 text-base font-medium rounded-md shadow-sm text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                 >
                    Back to Home
                 </Link>
                 {/* Commented out confirmation button remains */}
                 {/* {finalStage.id === 'COMPLETED' && finalOrderNumber && ( ... )} */}
                 </>
            )}
        </div>
    </div>
  );
}

export default OrderProcessingPage; 